import React from 'react';
import { Link } from 'react-router-dom';

/** Fixed legal effective date — update intentionally when terms change (do not use runtime locale dates). */
const LAST_UPDATED = 'May 4, 2026';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <Link to="/" className="text-blue-600 hover:text-blue-800 mb-4 inline-block">
            ← Back to Home
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms of Service</h1>
          <p className="text-gray-600">Last updated: {LAST_UPDATED}</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700">
              These Terms of Service (&quot;Terms&quot;) govern your access to and use of Devello Tech&apos;s websites,
              web application, APIs, and mobile applications (collectively, the &quot;Service&quot;). By creating an account,
              subscribing, downloading our apps, or otherwise using the Service, you agree to these Terms. If you do not
              agree, do not use the Service.
            </p>

            <h2>1. Who we are</h2>
            <p>
              The Service is provided by <strong>Devello Tech</strong> (&quot;Devello Tech,&quot; &quot;we,&quot;
              &quot;us,&quot; or &quot;our&quot;), operating the website <strong>devellotech.ca</strong> and related
              services. For questions about these Terms, contact us using the information in Section 16.
            </p>

            <h2>2. The Service</h2>
            <p>
              Devello Tech is a cloud software platform for construction and renovation contractors. Depending on your
              plan and product surface, features may include (without limitation):
            </p>
            <ul>
              <li>Invoice creation, management, and PDF generation or export</li>
              <li>Client and project (job) records, scheduling, and team tools</li>
              <li>Project workspace features such as change orders, notes, photos, documents, and job expenses (where
                available on your plan)</li>
              <li>Region-based material price search and related third-party data (Pro, where enabled)</li>
              <li>Email delivery of documents and service-related notifications (where you use those features)</li>
              <li>Business metrics and reporting (Pro, where available)</li>
              <li>Optional client portal features for your customers, where offered</li>
            </ul>
            <p>
              We may add, change, or retire features to improve the Service, comply with law, or reflect how the product
              is offered. If a change materially reduces rights you already paid for under an active paid subscription,
              we will use reasonable efforts to provide notice (for example by email or in-app notice) where
              practicable. Feature lists and limitations are also shown in the product and on our plans page and may
              change from time to time.
            </p>

            <h2>3. Eligibility and accounts</h2>
            <h3>3.1 Business use</h3>
            <p>
              The Service is intended for businesses and individuals acting in a business capacity. You must be at least
              the age of majority in your province, state, or country of residence, and able to form a binding contract.
            </p>
            <h3>3.2 Accurate information</h3>
            <p>
              You agree to provide accurate, current, and complete registration and billing information and to keep it
              updated. You are responsible for all activity on your account and for safeguarding your login credentials
              and devices.
            </p>
            <h3>3.3 No credential sharing</h3>
            <p>
              You may not share or sell access to your account, resell the Service without our written permission, or use
              the Service in a way that circumvents technical or plan limits.
            </p>
            <h3>3.4 Mobile application access</h3>
            <p>
              Our mobile app is offered as a companion to eligible paid subscriptions. <strong>Only users with an active
              Pro (or otherwise eligible) subscription and entitlements as determined in the product may use the mobile
              app.</strong> Paid account access and billing are managed through the Devello web platform. The mobile
              app does not process purchases, upgrades, billing, or subscription changes. If your subscription or access
              no longer qualifies, you may be signed out or unable to use the app until you restore eligible access.
              Installing the app through the Apple App Store or Google Play remains subject to those platforms&apos;
              applicable terms for app distribution.
            </p>

            <h2>4. Plans, fees, and payment</h2>
            <h3>4.1 Free and paid plans</h3>
            <p>
              We may offer a <strong>Free</strong> tier and a <strong>Pro</strong> (or similarly named) paid subscription
              with additional features. Plan names, features, and prices are described in the product and on our
              website and may change. Taxes and currency are as shown at Stripe checkout or on your payment receipt.
            </p>
            <h3>4.2 Paid subscriptions (Devello web platform)</h3>
            <p>
              Paid Devello Tech subscriptions are managed through the Devello web platform. Payment processing is handled
              by <strong>Stripe</strong> (or another card processor we designate). You authorize us and Stripe to charge
              your selected payment method for fees, taxes, and renewals. You can manage or cancel your subscription
              through the billing or customer portal available from your account on <strong>devellotech.ca</strong>,
              subject to Stripe and card network rules. The mobile app does not process purchases or subscription changes.
            </p>
            <h3>4.3 Promotional and trial access</h3>
            <p>
              We may, from time to time, offer trials, credits, or promotional access. Any such offer is subject to the
              terms presented at signup or checkout (including duration, eligibility, and conversion to paid plans).
              Unless explicitly stated otherwise, promotional access does not obligate us to maintain any particular
              price afterward.
            </p>
            <h3>4.4 Failed payments and suspension</h3>
            <p>
              If payment fails or cannot be processed, we may suspend or downgrade paid features until payment succeeds,
              subject to any grace period we choose to provide.
            </p>
            <h3>4.5 Price changes</h3>
            <p>
              We may change our subscription prices for new purchases or, where permitted by law and platform rules,
              for renewals with reasonable advance notice.
            </p>

            <h2>5. Your content and data</h2>
            <h3>5.1 Your content</h3>
            <p>
              You retain ownership of business content you submit (for example invoices, client names you enter, files,
              and images). You grant Devello Tech a worldwide, non-exclusive license to host, copy, process, transmit,
              display, and distribute your content only as needed to provide, secure, and improve the Service, comply with
              law, and as described in our Privacy Policy.
            </p>
            <h3>5.2 Responsibility</h3>
            <p>
              You are responsible for your content and for obtaining rights and consents needed for any personal
              information you upload about your clients, employees, or others. You represent that your use of the Service
              complies with applicable privacy, employment, and contracting laws. The Service does not provide legal,
              tax, or accounting advice; generated invoices and documents are tools for your business judgment only.
            </p>
            <h3>5.3 Client portal</h3>
            <p>
              If you invite end customers to a client portal or share links, you are responsible for the relationship with
              those users and for the data you collect from them, in addition to our role as a service provider to you.
            </p>

            <h2>6. Acceptable use</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Violate any law or third-party rights</li>
              <li>Upload malware, attempt unauthorized access, or probe or stress the Service in a way that could harm
                others</li>
              <li>Scrape, data-mine, or use automated means to extract data from the Service except through documented
                APIs or features we make available to you</li>
              <li>Send spam or use the Service to send unlawful or misleading communications</li>
              <li>Misrepresent your identity or affiliation, or use the Service to deceive others</li>
            </ul>
            <p>We may investigate and suspend or terminate access for conduct we reasonably believe breaches these Terms
              or creates risk to the Service or other users.</p>

            <h2>7. Third-party services</h2>
            <p>
              The Service relies on third parties (for example Stripe, cloud hosting and database providers, email
              delivery, error monitoring, and material pricing data providers). Your use of those services may be subject
              to their terms and privacy policies. We are not responsible for third-party services we do not control.
            </p>

            <h2>8. Intellectual property</h2>
            <p>
              The Service, including software, design, text, and branding, is owned by Devello Tech and its licensors and
              is protected by intellectual property laws. Except for the limited right to use the Service in line with
              these Terms, no rights are granted to you. Feedback you provide may be used by us without obligation to
              you.
            </p>

            <h2>9. Confidentiality and security</h2>
            <p>
              We implement reasonable technical and organizational measures to protect the Service, as further described
              in our Privacy Policy. No system is perfectly secure; you are responsible for maintaining the security of
              your account credentials and devices.
            </p>

            <h2>10. Disclaimers</h2>
            <p className="font-semibold">
              THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE.&quot; TO THE MAXIMUM EXTENT PERMITTED BY
              APPLICABLE LAW, DEVELLO TECH DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY,
              FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE
              UNINTERRUPTED, ERROR-FREE, OR FREE OF HARMFUL COMPONENTS, OR THAT DATA WILL BE COMPLETE OR ACCURATE.
            </p>

            <h2>11. Limitation of liability</h2>
            <p className="font-semibold">
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, DEVELLO TECH AND ITS DIRECTORS, EMPLOYEES, AND
              SUPPLIERS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE
              DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, GOODWILL, OR BUSINESS OPPORTUNITY, ARISING FROM OR RELATED
              TO THE SERVICE OR THESE TERMS, WHETHER OR NOT WE WERE ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
            </p>
            <p className="font-semibold">
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, OUR TOTAL LIABILITY FOR ANY CLAIMS ARISING OUT OF OR
              RELATING TO THE SERVICE OR THESE TERMS IN ANY TWELVE-MONTH PERIOD IS LIMITED TO THE GREATER OF (A) THE
              AMOUNTS YOU PAID TO DEVELLO TECH FOR THE SERVICE IN THAT PERIOD, OR (B) ONE HUNDRED CANADIAN DOLLARS
              (CA$100), IF YOU HAD NO FEES DURING THAT PERIOD. SOME JURISDICTIONS DO NOT ALLOW CERTAIN LIMITATIONS; IN
              THOSE CASES, OUR LIABILITY IS LIMITED TO THE FULLEST EXTENT PERMITTED BY LAW.
            </p>

            <h2>12. Indemnity</h2>
            <p>
              You will defend, indemnify, and hold harmless Devello Tech from claims, damages, liabilities, and expenses
              (including reasonable legal fees) arising from your content, your use of the Service in breach of these
              Terms, or your violation of applicable law or third-party rights, except to the extent caused by our
              willful misconduct or gross negligence.
            </p>

            <h2>13. Termination</h2>
            <p>
              You may stop using the Service at any time. We may suspend or terminate access if you materially breach
              these Terms, create legal or security risk, or where we cease offering all or part of the Service. Sections
              that by their nature should survive (including intellectual property, disclaimers, limitations of
              liability, indemnity, and dispute resolution) survive termination.
            </p>
            <p>
              After termination, we may delete or retain data as described in our Privacy Policy and as required by law.
              Where the product offers export features, you should export important data before closing your account.
            </p>

            <h2>14. Governing law and disputes</h2>
            <p>
              These Terms are governed by the laws of the <strong>Province of Ontario</strong> and the federal laws of
              Canada applicable therein, without regard to conflict-of-law rules. Subject to mandatory consumer rights in
              your jurisdiction, you agree that the courts located in Ontario have exclusive jurisdiction over disputes
              not subject to arbitration below, except that either party may seek injunctive relief in any court of
              competent jurisdiction.
            </p>
            <p>
              For disputes over CA$500 (excluding interest and costs), either party may elect binding arbitration under
              the arbitration rules of a recognized arbitration administrator operating in Canada, conducted in English,
              with one arbitrator, held in Guelph or Toronto, Ontario, unless you and we agree otherwise in writing.
              Arbitration shall proceed on an individual basis only; neither party will participate in a class or
              representative action in arbitration or court (except where prohibited by applicable law).
            </p>

            <h2>15. Changes to these Terms</h2>
            <p>
              We may modify these Terms by posting an updated version on this page and updating the &quot;Last
              updated&quot; date. If a change is material, we will provide reasonable notice where required by law (for
              example by email or in-product notice). Continued use after the effective date of changes constitutes
              acceptance of the revised Terms, except where your explicit consent is required by law.
            </p>

            <h2>16. Contact</h2>
            <p>For questions about these Terms:</p>
            <div className="bg-gray-50 p-4 rounded-lg not-prose text-gray-900">
              <p className="font-semibold">Devello Tech</p>
              <p>Email (all inquiries): info@devellotech.ca</p>
              <p>Website: https://devellotech.ca</p>
              <p>Address: Guelph, Ontario, Canada</p>
              <p className="text-sm text-gray-600 mt-2">
                For legal notices, email info@devellotech.ca with &quot;Legal Notice&quot; in the subject line.
              </p>
            </div>

            <h2>17. Severability; entire agreement</h2>
            <p>
              If any provision is held invalid, the remaining provisions remain in effect. These Terms, together with
              our Privacy Policy and any order or checkout terms presented at purchase, constitute the entire agreement
              between you and Devello Tech regarding the Service.
            </p>

            <p className="text-sm text-gray-600 mt-8">
              <strong>General.</strong> Nothing in these Terms is legal advice. If you need advice about how these Terms
              apply to your business, consult a qualified lawyer in your jurisdiction.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
